import gettext from '@/i18n/gettext';
import { computed } from 'vue';

const { $gettext } = gettext;

const requiredRule = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') {
    return $gettext('Field is required');
  }
  return true;
};

const selectFieldRequiredRule = (v: string | null) =>
  (v !== null && v !== '') || $gettext('Field is required');

const serialNumberRegexRule = (v: string) => {
  const pattern = /^SN[0-9]{2}-[0-9]{7}$/;
  return (
    pattern.test(v) ||
    $gettext(
      "Wrong serial number format. Must be 'SNxx-xxxxxxx' where 'x' is a digit and SN is uppercase",
    )
  );
};

export const useObjectSizeRule = (min: number, max: number) => {
  return computed(() => {
    return (v: number) => {
      return !Number.isNaN(v) && v >= min && v <= max;
    };
  });
};

const noAmpersandRegexRule = (v: string) => {
  const pattern = /&/;
  return !pattern.test(v) || $gettext("'&' signs are not allowed");
};

const emailValidationRule = (v: string) => {
  const pattern =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return pattern.test(v) || $gettext('Invalid e-mail address');
};

const floatRule = (v: string | number | null | undefined) => {
  if (v === null || v === undefined || v === '') {
    return true;
  }
  return (
    (typeof v === 'number' && !Number.isNaN(v)) || $gettext('Must be a number')
  );
};

const positiveFloatRule = (v: number | null | undefined) => {
  if (v === null || v === undefined) {
    return true;
  }
  return (
    (typeof v === 'number' && v >= 0) || $gettext('Must be a positive number')
  );
};

const integerRule = (v: number | null | undefined) => {
  if (v === null || v === undefined) {
    return true;
  }
  return (
    (typeof v === 'number' && Number.isInteger(v)) ||
    $gettext('Must be a whole number')
  );
};

const positiveIntegerRule = (v: number | null | undefined) => {
  if (v === null || v === undefined) {
    return true;
  }
  return (
    (typeof v === 'number' && Number.isInteger(v) && v > 0) ||
    $gettext('Must be a positive whole number')
  );
};

const noSpacesRule = (v: string | null | undefined) => {
  if (v === null || v === undefined) {
    return true;
  }
  return (
    (typeof v === 'string' && !v.includes(' ')) ||
    $gettext('Must not contain spaces')
  );
};

const imageProjectMaximumFileSizeRule = (v: File[] | null | undefined) => {
  if (v && Array.isArray(v)) {
    const tooLarge = v.some((file) => file.size >= 15 * 1024 * 1024);
    return !tooLarge || $gettext('File exceeds maximum size of 15MB');
  }
  return true;
};

const imageProjectmaximumFilesRule = (v: File[] | null | undefined) => {
  if (v && Array.isArray(v)) {
    if (v.length > 15) {
      return $gettext('Maximum 15 files allowed');
    }
  }
  return true;
};

const ipClassRule = (v: string | null | undefined) => {
  if (v === null || v === undefined || v === '') {
    return true;
  }
  const pattern = /^IP[0-6X][0-9X]$/;
  return (
    pattern.test(v) ||
    $gettext(
      "Invalid IP class format. Must be 'IPxy' where x is 0-6 or X and y is 0-9 or X",
    )
  );
};

const numericOnlyRule = (v: string | null | undefined) => {
  if (v === null || v === undefined || v === '') {
    return true;
  }
  return /^\d+$/.test(v) || $gettext('Must contain only digits');
};

export {
  requiredRule,
  numericOnlyRule,
  floatRule,
  selectFieldRequiredRule,
  serialNumberRegexRule,
  noAmpersandRegexRule,
  emailValidationRule,
  positiveFloatRule,
  integerRule,
  positiveIntegerRule,
  noSpacesRule,
  imageProjectMaximumFileSizeRule,
  imageProjectmaximumFilesRule,
  ipClassRule,
};
